import { parseOutline } from '../outline';
import { slugifyHeading } from './slug';

/**
 * Page setup for PDF export, and the optional table of contents.
 *
 * Kept as pure functions over strings so the layout can be tested without a
 * print dialog — the one part of PDF export no test can drive.
 */

export type PageSize = 'auto' | 'A4' | 'Letter';
export type PageMargins = 'narrow' | 'normal' | 'wide';

export const PAGE_SIZES: PageSize[] = ['auto', 'A4', 'Letter'];
export const PAGE_MARGINS: PageMargins[] = ['narrow', 'normal', 'wide'];

/** [vertical, horizontal] padding — the page itself has no margin, see printCss. */
const PADDING: Record<PageMargins, [string, string]> = {
  narrow: ['1cm', '1.1cm'],
  normal: ['1.6cm', '1.8cm'],
  wide: ['2.5cm', '2.8cm'],
};

/**
 * `@page` keeps a zero margin: that is what removes the browser's own header and
 * footer (title, date, URL). The chosen margin is applied as padding instead.
 * "auto" leaves the paper size to the print dialog, as before.
 */
export function printCss({
  pageSize,
  margins,
}: {
  pageSize: PageSize;
  margins: PageMargins;
}): string {
  const [v, h] = PADDING[margins];
  return `
@page { margin: 0; size: ${pageSize === 'auto' ? 'auto' : pageSize}; }
@media print {
  html, body { margin: 0; }
  .markbird-preview {
    max-width: none;
    margin: 0;
    padding: ${v} ${h} 0.6cm;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .mb-export-footer { margin: 1.5rem 0 0; padding: 0.8rem ${h} 1.2cm; }
  .mb-export-toc { break-after: page; }
  pre, blockquote, table, .mermaid, .katex-display { break-inside: avoid; }
  h1, h2, h3 { break-after: avoid; }
}
`;
}

/**
 * Prepend a linked table of contents built from the document's headings.
 *
 * The outline comes from the Markdown (the same parser the outline panel uses,
 * so headings inside code blocks are skipped identically), and the rendered
 * headings receive matching ids in document order so every entry links to its
 * section. Levels 1–3 only: a contents page that lists every h5 is not read.
 * A document with fewer than two headings gets no contents page at all.
 */
export function withTableOfContents(bodyHtml: string, markdown: string, heading: string): string {
  const outline = parseOutline(markdown).filter((h) => h.level <= 3);
  if (outline.length < 2) return bodyHtml;

  const dom = new DOMParser().parseFromString(`<div>${bodyHtml}</div>`, 'text/html');
  const root = dom.body.firstElementChild as HTMLElement;
  const rendered = [...root.querySelectorAll('h1, h2, h3')];

  const seen = new Map<string, number>();
  const idFor = (text: string): string => {
    const base = slugifyHeading(text) || 'section';
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}-${n + 1}`;
  };

  const items = outline.map((h, i) => {
    const id = idFor(h.text);
    const el = rendered[i];
    if (el) el.id = id;
    return { ...h, id };
  });

  const minLevel = Math.min(...items.map((i) => i.level));
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const list = items
    .map(
      (i) =>
        `<li class="mb-export-toc-l${i.level - minLevel + 1}"><a href="#${i.id}">${escape(i.text)}</a></li>`,
    )
    .join('');

  return `<nav class="mb-export-toc" aria-label="${escape(heading)}"><h2>${escape(heading)}</h2><ol>${list}</ol></nav>${root.innerHTML}`;
}
